import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import {
  MAT_TOOLTIP_DEFAULT_OPTIONS,
  MatTooltipDefaultOptions,
  MatTooltipModule,
} from '@angular/material/tooltip';
import { FileService } from '@ipg/ngx-frontend-toolset';
import { isSameDay } from 'date-fns';

import { ScmClickBlockStatistics } from '../../services/client-statistics.service';

const HOURS_PER_DAY = 24;

/** Number of steps in the sequential colour ramp. */
const RAMP_STEPS = 5;

/**
 * Tooltip behaviour for the grid, overriding the app-wide options.
 *
 * Reading the grid means sweeping across cells to compare them, so the tooltip
 * has to keep up with the pointer: no delay in either direction (the app default
 * is 300ms to show), and `disableTooltipInteractivity` so the panel cannot take
 * the pointer. Without that last one the panel is hoverable, and moving down one
 * row lands on the panel of the row you just left — which both keeps the stale
 * tooltip open and hides the cell you were aiming for. Made non-interactive the
 * pointer falls through to that cell and the tooltip swaps over.
 *
 * `touchendHideDelay` is Material's own default, restated because the token is
 * replaced wholesale rather than merged.
 */
const TOOLTIP_OPTIONS: MatTooltipDefaultOptions = {
  showDelay: 0,
  hideDelay: 0,
  touchendHideDelay: 1500,
  disableTooltipInteractivity: true,
};

const DAY_FORMAT = new Intl.DateTimeFormat('nl-NL', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

/**
 * What a single cell means.
 *
 * `clicks` and `zero` carry a count; `unknown` and `pending` carry none. The
 * last two are kept apart because they resolve differently: a `pending` cell
 * becomes readable simply by waiting for the hour to finish, while an `unknown`
 * one never will.
 */
export type ScmHeatmapCellState = 'clicks' | 'zero' | 'unknown' | 'pending';

export interface ScmHeatmapCell {
  hour: number;
  state: ScmHeatmapCellState;
  clicks: number;
  /** 1..RAMP_STEPS for `clicks`, 0 otherwise. */
  level: number;
  label: string;
}

export interface ScmHeatmapRow {
  block: ScmClickBlockStatistics;
  avatarUrl: string | null;
  cells: ScmHeatmapCell[];
}

/**
 * One day of SCM block clicks: one row per block, 24 hour columns.
 *
 * Deliberately a single day rather than an aggregate over a period. Summing
 * across days answers "when does this client usually engage" but destroys the
 * day itself — five clicks at 09:00 could be five yesterday or one on each of
 * five days, and "today" disappears entirely. Stepping day by day keeps both
 * readings available: the day is evidence, and the pattern is what you see by
 * stepping through consecutive days.
 *
 * Built as a table rather than a chart because the interesting part is not the
 * magnitude but *which cells are comparable at all*. Blocks are activated and
 * deactivated during the day, so a numeric reading is only offered where the
 * block is positively known to have been active for that whole hour;
 * everywhere else means nothing at all.
 *
 * That "nothing" comes in two kinds and they are drawn differently. Hours whose
 * presence cannot be established — deactivated, deleted, activated mid-hour, or
 * predating lifecycle logging — are hatched. Hours that have not finished yet on
 * today are left empty instead: the block may well be active this very minute,
 * so hatching them would read as "the block was not there", which is the one
 * thing that is certainly false. They still cannot hold a zero, because a zero
 * over half an hour is not comparable to a zero over a whole one — it is the
 * reading that is missing, not the block.
 *
 * Cells never print their value — the four states are carried by fill and
 * texture, and the counts live in the tooltip.
 */
@Component({
  selector: 'app-scm-click-heatmap',
  imports: [MatTooltipModule],
  templateUrl: './scm-click-heatmap.component.html',
  styleUrl: './scm-click-heatmap.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Provided here rather than in `app.config.ts`: every tooltip in the grid
  // needs this, and no tooltip outside it should be affected.
  providers: [{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: TOOLTIP_OPTIONS }],
})
export class ScmClickHeatmapComponent {
  private readonly _file = inject(FileService);

  public readonly blocks = input<ScmClickBlockStatistics[]>([]);

  /** The day on show. Only used to name the day in the accessible caption. */
  public readonly day = input<Date | null>(null);

  protected readonly hours = Array.from({ length: HOURS_PER_DAY }, (_, hour) => hour);

  protected readonly rampSteps = Array.from(
    { length: RAMP_STEPS },
    (_, step) => step + 1
  );

  /** Scale shared by every row, so cells stay comparable between blocks. */
  protected readonly scaleMax = computed(() =>
    Math.max(0, ...this.blocks().flatMap((block) => block.hours))
  );

  protected readonly rows = computed<ScmHeatmapRow[]>(() =>
    this.blocks().map((block) => ({
      block,
      avatarUrl: block.avatarFileId ? this._file.get(block.avatarFileId).href : null,
      cells: block.hours.map((clicks, hour) => this.toCell(block, clicks, hour)),
    }))
  );

  protected readonly dayLabel = computed(() => {
    const day = this.day();

    return day ? DAY_FORMAT.format(day) : 'de gekozen dag';
  });

  protected readonly totalClicks = computed(() =>
    this.blocks().reduce((total, block) => total + block.totalClicks, 0)
  );

  /**
   * First hour of the day on show that has not finished yet, or null when the
   * whole day is behind us.
   *
   * The running hour counts as pending: it is the hour the backend cannot credit
   * as presence yet, since presence is only granted for *whole* elapsed hours.
   * Read from the browser clock, which is the same wall clock the day itself was
   * built from and the timezone the counts were bucketed in.
   */
  protected readonly pendingFromHour = computed<number | null>(() => {
    const day = this.day();

    if (!day) {
      return null;
    }

    const now = new Date();

    return isSameDay(day, now) ? now.getHours() : null;
  });

  protected formatHour(hour: number): string {
    return `${hour}`.padStart(2, '0');
  }

  private toCell(
    block: ScmClickBlockStatistics,
    clicks: number,
    hour: number
  ): ScmHeatmapCell {
    // For a single day this is 0 or 1: was the block active for this whole
    // local hour? Hours still to come today are never marked.
    const wasActive = (block.presentDaysPerHour[hour] ?? 0) > 0;
    // The cell covers a whole hour, so name both ends — "11:00" alone reads as
    // the moment the clicks happened rather than the block they fall in.
    const at = `${this.formatHour(hour)}:00–${this.formatHour(
      (hour + 1) % HOURS_PER_DAY
    )}:00`;

    // A click is proof the block was there, whether or not the hour is whole
    // enough for presence to have been credited — so counts come first.
    if (clicks > 0) {
      return {
        hour,
        state: 'clicks',
        clicks,
        level: this.toRampLevel(clicks),
        label: `${at} – ${clicks} ${clicks === 1 ? 'klik' : 'kliks'}`,
      };
    }

    // Presence is settled by the backend, so an hour it credits is a real zero
    // even if the browser clock still considers it unfinished.
    if (wasActive) {
      return {
        hour,
        state: 'zero',
        clicks,
        level: 0,
        label: `${at} – actief, geen kliks`,
      };
    }

    const pendingFrom = this.pendingFromHour();

    if (pendingFrom !== null && hour >= pendingFrom) {
      return {
        hour,
        state: 'pending',
        clicks,
        level: 0,
        // "Nog geen kliks" is all that can be claimed: true so far, and without
        // the finality that would make it comparable to a finished hour.
        label:
          hour === pendingFrom
            ? `${at} – nog geen kliks (dit uur loopt nog)`
            : `${at} – dit uur moet nog komen`,
      };
    }

    // Not active, or predating lifecycle logging — indistinguishable, and in
    // each case a count here would be unfounded.
    return {
      hour,
      state: 'unknown',
      clicks,
      level: 0,
      label: `${at} – niet van toepassing (blok niet aantoonbaar actief)`,
    };
  }

  /**
   * Place a click count on the ramp, relative to the busiest hour on show.
   *
   * The interpolation runs over `1..max` rather than `0..max`, because 1 is the
   * smallest value a painted cell can hold — a cell with no clicks is a
   * different state and never reaches here. Measuring against a floor of 0
   * wastes the bottom of the ramp on counts that cannot occur: it made the
   * palest step unreachable on any day whose peak was under 5, and painted a
   * lone single click in the darkest teal of all, since it was simultaneously
   * the smallest and the largest value on the grid.
   *
   * So the smallest count present is always the palest step and the peak always
   * the darkest, whatever the day's volume. Colour therefore compares hours
   * *within* one day and says nothing across days — which is the same bargain
   * the shared scale already makes between blocks, and why the counts
   * themselves stay in the tooltip.
   */
  private toRampLevel(clicks: number): number {
    const max = this.scaleMax();

    if (max <= 1) {
      return 1;
    }

    const position = (clicks - 1) / (max - 1);

    return 1 + Math.round(position * (RAMP_STEPS - 1));
  }
}
